import { Button, Dropdown, type FormItemProps, Input, type InputProps } from 'antd';
import { useField } from 'formik';
import { trimStart, padStart } from 'lodash';
import { useCallback } from 'react';
import { ChromePicker, type ChromePickerProps } from 'react-color';
import FormItem from './FormItem';

export interface ColorPickerFieldProps extends Omit<
    InputProps,
    'value' | 'onChange' | 'autoComplete' | 'addonBefore' | 'suffix'
> {
    name: string;
    label?: string;
    itemProps?: FormItemProps;
}

type ColorPickerFieldSuffixProps = {
    color: string;
    onChangeComplete: ChromePickerProps['onChangeComplete'];
};

const ColorPickerFieldSuffix = ({ color, onChangeComplete }: ColorPickerFieldSuffixProps) => (
    <Dropdown overlay={<ChromePicker color={color} onChangeComplete={onChangeComplete} />} trigger={['click']}>
        <Button size="small" style={{ backgroundColor: color, width: '22px', height: '22px' }}>
            {' '}
        </Button>
    </Dropdown>
);

const getColorHex = (hex: string, alpha?: number) => {
    const trimmed = trimStart(hex, '#');

    if (alpha !== undefined && alpha >= 0 && alpha < 1) {
        const alphaHex = Math.round(alpha * 0xff).toString(16);

        return `${trimmed}${padStart(alphaHex, 2, '0')}`;
    }

    return trimmed;
};

const ColorPickerField = ({ name, label, required, itemProps, ...props }: ColorPickerFieldProps) => {
    const [field, meta, helpers] = useField({ name });
    const onChangeComplete = useCallback<ColorPickerFieldSuffixProps['onChangeComplete']>(
        ({ hex, rgb: { a } }) => {
            helpers.setValue(getColorHex(hex, a));
        },
        [helpers]
    );

    return (
        <FormItem {...itemProps} label={label} meta={meta} required={required}>
            <Input
                addonBefore="#"
                autoComplete="off"
                required={required}
                suffix={<ColorPickerFieldSuffix color={`#${field.value}`} onChangeComplete={onChangeComplete} />}
                // spread props
                {...props}
                // then spread the field properties itself
                {...field}
            />
        </FormItem>
    );
};

export default ColorPickerField;
